/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import {ColumnState} from 'ag-grid-community';
import produce from 'immer';
import {reInitializeState} from '@ion/applications/src/helpers';
import {
  Asset,
  DeviceMove
} from '@ion/core/src/types/entities';
import {ServiceError} from '@ion/core/src/types/services';
import {FilterModel} from '@ion/dls/src/grid/IonGridUtils';
import {PageState} from '@/types/pages';
import {
  AdminAction,
  AsyncState
} from '@/types/redux';
import {
  ADD_ADMIN_ASSET,
  ADD_ADMIN_ASSET_ERROR,
  ADD_ADMIN_ASSET_REQUEST,
  ADD_ADMIN_ASSET_RESPONSE,
  ADMIN_ASSET_VEHICLE_CONTROL,
  ADMIN_ASSET_VEHICLE_CONTROL_ERROR,
  ADMIN_ASSET_VEHICLE_CONTROL_HIDE,
  ADMIN_ASSET_VEHICLE_CONTROL_SHOW,
  ADMIN_ASSETS_GRID_REFRESHED,
  AUDIT_ADMIN_ASSET,
  DELETE_ADMIN_ASSET,
  DELETE_ADMIN_ASSET_ERROR,
  DELETE_ADMIN_ASSET_REQUEST,
  DELETE_ADMIN_ASSET_RESPONSE,
  DELETE_ADMIN_ASSETS,
  DELETE_ADMIN_ASSETS_ERROR,
  DELETE_ADMIN_ASSETS_REQUEST,
  DELETE_ADMIN_ASSETS_RESPONSE,
  EDIT_ADMIN_ASSET,
  EDIT_ADMIN_ASSET_ERROR,
  EDIT_ADMIN_ASSET_REQUEST,
  EDIT_ADMIN_ASSET_RESPONSE,
  EDIT_ADMIN_ASSETS,
  EDIT_ADMIN_ASSETS_ERROR,
  EDIT_ADMIN_ASSETS_REQUEST,
  EDIT_ADMIN_ASSETS_RESPONSE,
  GET_ADMIN_ASSETS_ERROR,
  GET_ADMIN_ASSETS_REQUEST,
  GET_ADMIN_ASSETS_RESPONSE,
  IMPORT_ADMIN_ASSETS,
  INITIALIZE_ADMIN_ASSETS,
  MANAGE_ADMIN_ASSET,
  MANAGE_ADMIN_ASSET_ERROR,
  MANAGE_ADMIN_ASSET_REQUEST,
  MANAGE_ADMIN_ASSET_RESPONSE,
  RE_INITIALIZE_ADMIN_ASSETS,
  SET_ADMIN_ASSETS_GRID_FILTER_MODEL,
  SET_CURRENT_MOVE_TO_ACCOUNT,
  SET_GRID_COLUMNS,
  UPDATE_ADMIN_ASSET,
  UPDATE_ADMIN_ASSET_ERROR,
  UPDATE_ADMIN_ASSET_REQUEST,
  UPDATE_ADMIN_ASSET_RESPONSE,
  UPDATE_GRID_COLUMNS
} from '../actions/admin-assets';
import {
  getGridColumnState,
  onSetGridColumns,
  onUpdateGridColumns
} from '../helpers';

export interface AdminAssetAction extends AdminAction {
  asset?: Asset;
  assets?: Asset[];
  assignment?: String;
};

/**
 * Defines the shape of each admin asset edit state; i.e. add, edit, delete,
 * bulk edit, bulk delete.
 *
 * This type extends AsyncState which provides the loading and error states
 * associated with the action, and adds the following:
 *
 * - selected: Defines a reference to the asset or assets that are to be edited
 *   or deleted
 *
 * - selected: Defines a reference to the asset or assets that are to be edited
 *   or deleted
 * - result: Defines a reference to the service results after the selected item(s)
 *   have been updated or deleted.
 */
interface AdminAssetActionState extends AsyncState {
  assignment?: String;
  selected: Asset | Asset[] | null;
  result: Asset | Asset[] | null;
  isEditing: boolean;
  record?: Asset | Asset[] | null;
};

interface AdminAssetImportState {
  isImporting: boolean;
  result: Asset | Asset[] | null;
};

interface AdminAssetVehicleControlState {
  showMessage: boolean;
  showErrorMessage: boolean;
  selected: Asset | null;
  result: Asset | null;
};

export interface AdminAssetsState extends PageState {
  assets: {
    error: ServiceError;
    loading: boolean;
  },
  grid: {
    columnState: ColumnState[],
    preferedColumnState: ColumnState[],
    hasUpdate: boolean,
    filterModel: FilterModel | {},
  },
  add: AdminAssetActionState,
  edit: AdminAssetActionState,
  manage: AdminAssetActionState,
  update: AdminAssetActionState,
  audit: AdminAssetActionState,
  vehicleControl: AdminAssetVehicleControlState,
  destroy: AdminAssetActionState,
  bulkEdit: AdminAssetActionState,
  bulkDelete: AdminAssetActionState,
  bulkImport: AdminAssetImportState,
  movedToAccount: DeviceMove,
}

// defines the preference key for the admin assets page ...
const PREFERENCE = 'admin-assets';

// defines the initial state for the reducer ...
export const initialState: AdminAssetsState = {
  isInitialized : false,
  assets        : {
    error   : null,
    loading : false
  },
  grid : {
    columnState         : null,
    preferedColumnState : null,
    hasUpdate           : false,
    filterModel         : {}
  },
  add : {
    isEditing : false,
    selected  : null,
    loading   : false,
    error     : null,
    result    : null
  },
  edit : {
    isEditing : false,
    selected  : null,
    loading   : false,
    error     : null,
    result    : null
  },
  manage : {
    isEditing : false,
    selected  : null,
    loading   : false,
    error     : null,
    result    : null
  },
  update : {
    isEditing  : false,
    selected   : null,
    loading    : false,
    error      : null,
    result     : null,
    assignment : null
  },
  audit : {
    isEditing : false,
    selected  : null,
    loading   : false,
    error     : null,
    result    : null
  },
  vehicleControl : {
    selected         : null,
    showMessage      : false,
    showErrorMessage : false,
    result           : null
  },
  destroy : {
    isEditing : false,
    selected  : null,
    loading   : false,
    error     : null,
    result    : null
  },
  bulkEdit : {
    isEditing : false,
    selected  : null,
    loading   : false,
    error     : null,
    result    : null
  },
  bulkDelete : {
    isEditing : false,
    selected  : null,
    loading   : false,
    error     : null,
    result    : null,
    record    : null
  },
  bulkImport : {
    isImporting : false,
    result      : null
  },
  movedToAccount : {
    selectedAccount : null
  }
};

// defines this reducers reducer functions ...
const reducers = {
  [INITIALIZE_ADMIN_ASSETS] : draft => {
    draft.isInitialized            = true;
    const preference               = getGridColumnState(PREFERENCE, true);
    draft.grid.columnState         = preference;
    draft.grid.preferedColumnState = preference;
    draft.destroy.error            = null;
  },
  [RE_INITIALIZE_ADMIN_ASSETS] : draft => {
    reInitializeState(draft, {initialState});
  },
  [SET_GRID_COLUMNS] : ({grid, assets}: AdminAssetsState, {columnState}) => {
    onSetGridColumns({
      grid,
      isLoading  : assets.loading,
      preference : PREFERENCE
    }, columnState);
  },
  [UPDATE_GRID_COLUMNS] : ({grid}: AdminAssetsState, {columnState}) => {
    onUpdateGridColumns({grid, preference: PREFERENCE}, columnState);
  },
  [ADMIN_ASSETS_GRID_REFRESHED] : (
    {add, edit, manage, update, audit, vehicleControl, destroy, bulkEdit, bulkDelete, bulkImport}: AdminAssetsState
  ) => {
    add.result = null;
    edit.result = null;
    manage.result = null;
    update.result = null;
    audit.result = null;
    destroy.result = null;
    bulkEdit.result = null;
    bulkDelete.result = null;
    bulkImport.result = null;
    vehicleControl.showMessage = false;
    vehicleControl.showErrorMessage = false;
    vehicleControl.selected = null;
    vehicleControl.result = null;
  },
  [GET_ADMIN_ASSETS_REQUEST] : ({assets}: AdminAssetsState) => {
    assets.loading = true;
  },
  [GET_ADMIN_ASSETS_RESPONSE] : ({assets}: AdminAssetsState) => {
    assets.loading = false;
  },
  [GET_ADMIN_ASSETS_ERROR] : ({assets}: AdminAssetsState, {error}: AdminAssetAction) => {
    assets.error = error;
    assets.loading = false;
  },

  [ADD_ADMIN_ASSET] : ({add}: AdminAssetsState, {show}: AdminAssetAction) => {
    add.isEditing = show;
    add.result = null;
  },
  [ADD_ADMIN_ASSET_REQUEST] : ({add}: AdminAssetsState) => {
    add.loading = true;
    add.error = add.result = null;
  },
  [ADD_ADMIN_ASSET_RESPONSE] : ({add}: AdminAssetsState, {asset}: AdminAssetAction) => {
    add.loading = false;
    add.isEditing = false;
    add.result = asset;
  },
  [ADD_ADMIN_ASSET_ERROR] : ({add}: AdminAssetsState, {error}: AdminAssetAction) => {
    add.loading = false;
    add.error = error;
  },
  [IMPORT_ADMIN_ASSETS] : ({bulkImport}: AdminAssetsState, {show, result}: AdminAssetAction) => {
    bulkImport.isImporting = show;
    bulkImport.result = result;
  },
  [EDIT_ADMIN_ASSET] : ({edit}: AdminAssetsState, {asset, show}: AdminAssetAction) => {
    edit.selected = show ? asset : null;
    edit.isEditing = show;
    edit.result = null;
  },
  [EDIT_ADMIN_ASSET_REQUEST] : ({edit}: AdminAssetsState) => {
    edit.loading = true;
    edit.error = edit.result = null;
  },
  [EDIT_ADMIN_ASSET_RESPONSE] : ({edit}: AdminAssetsState, {asset}: AdminAssetAction) => {
    edit.loading = false;
    edit.isEditing = false;
    edit.result = asset;
  },
  [EDIT_ADMIN_ASSET_ERROR] : ({edit}: AdminAssetsState, {error}: AdminAssetAction) => {
    edit.loading = false;
    edit.error = error;
  },

  [MANAGE_ADMIN_ASSET] : ({manage}: AdminAssetsState, {asset, show}: AdminAssetAction) => {
    manage.selected = show ? asset : null;
    manage.isEditing = show;
    manage.result = null;
  },
  [MANAGE_ADMIN_ASSET_REQUEST] : ({manage}: AdminAssetsState) => {
    manage.loading = true;
    manage.error = manage.result = null;
  },
  [MANAGE_ADMIN_ASSET_RESPONSE] : ({manage}: AdminAssetsState, {asset}: AdminAssetAction) => {
    manage.loading = false;
    manage.isEditing = false;
    manage.result = asset;
  },
  [MANAGE_ADMIN_ASSET_ERROR] : ({manage}: AdminAssetsState, {error}: AdminAssetAction) => {
    manage.loading = false;
    manage.error = error;
  },

  [UPDATE_ADMIN_ASSET] : ({update}: AdminAssetsState, {asset, assignment, show}: AdminAssetAction) => {
    update.selected = show ? asset : null;
    update.assignment = assignment;
    update.isEditing = show;
    update.result = null;
  },
  [UPDATE_ADMIN_ASSET_REQUEST] : ({update}: AdminAssetsState) => {
    update.loading = true;
    update.error = update.result = null;
  },
  [UPDATE_ADMIN_ASSET_RESPONSE] : ({update}: AdminAssetsState, {asset}: AdminAssetAction) => {
    update.loading = false;
    update.isEditing = false;
    update.result = asset;
  },
  [UPDATE_ADMIN_ASSET_ERROR] : ({update}: AdminAssetsState, {error}: AdminAssetAction) => {
    update.loading = false;
    update.error = error;
  },
  [AUDIT_ADMIN_ASSET] : ({audit}: AdminAssetsState, {asset, show}: AdminAssetAction) => {
    audit.selected = show ? asset : null;
    audit.isEditing = show;
  },
  [ADMIN_ASSET_VEHICLE_CONTROL] : ({vehicleControl}: AdminAssetsState, {asset}: AdminAssetAction) => {
    vehicleControl.showMessage = false;
    vehicleControl.showErrorMessage = false;
    vehicleControl.selected = null;
    vehicleControl.result = asset;
  },
  [ADMIN_ASSET_VEHICLE_CONTROL_SHOW] : ({vehicleControl}: AdminAssetsState, {asset}: AdminAssetAction) => {
    vehicleControl.showMessage = true;
    vehicleControl.showErrorMessage = false;
    vehicleControl.selected = asset;
    vehicleControl.result = null;
  },
  [ADMIN_ASSET_VEHICLE_CONTROL_ERROR] : ({vehicleControl}: AdminAssetsState) => {
    vehicleControl.showMessage = false;
    vehicleControl.showErrorMessage = true;
    vehicleControl.selected = null;
    vehicleControl.result = null;
  },
  [ADMIN_ASSET_VEHICLE_CONTROL_HIDE] : ({vehicleControl}: AdminAssetsState) => {
    vehicleControl.showMessage = false;
    vehicleControl.showErrorMessage = false;
    vehicleControl.selected = null;
    vehicleControl.result = null;
  },
  [DELETE_ADMIN_ASSET] : ({destroy}: AdminAssetsState, {asset, show}: AdminAssetAction) => {
    destroy.selected = show ? asset : null;
    destroy.isEditing = show;
    destroy.result = null;
  },
  [DELETE_ADMIN_ASSET_REQUEST] : ({destroy}: AdminAssetsState) => {
    destroy.loading = true;
    destroy.error = destroy.result = null;
  },
  [DELETE_ADMIN_ASSET_RESPONSE] : ({destroy}: AdminAssetsState, {asset}: AdminAssetAction) => {
    destroy.loading = false;
    destroy.isEditing = false;
    destroy.result = asset;
  },
  [DELETE_ADMIN_ASSET_ERROR] : ({destroy}: AdminAssetsState, {error}: AdminAssetAction) => {
    destroy.loading = false;
    destroy.error = error;
    destroy.result = null;
  },

  [EDIT_ADMIN_ASSETS] : ({bulkEdit}: AdminAssetsState, {assets, show}: AdminAssetAction) => {
    bulkEdit.selected = show ? assets : null;
    bulkEdit.isEditing = show;
    bulkEdit.result = null;
  },
  [EDIT_ADMIN_ASSETS_REQUEST] : ({bulkEdit}: AdminAssetsState) => {
    bulkEdit.loading = true;
    bulkEdit.error = bulkEdit.result = null;
  },
  [EDIT_ADMIN_ASSETS_RESPONSE] : ({bulkEdit}: AdminAssetsState, {assets}: AdminAssetAction) => {
    bulkEdit.loading = false;
    bulkEdit.isEditing = false;
    bulkEdit.result = assets;
  },
  [EDIT_ADMIN_ASSETS_ERROR] : ({bulkEdit}: AdminAssetsState, {error}: AdminAssetAction) => {
    bulkEdit.loading = false;
    bulkEdit.error = error;
  },

  [DELETE_ADMIN_ASSETS] : ({bulkDelete}: AdminAssetsState, {assets, show}: AdminAssetAction) => {
    bulkDelete.selected = show ? assets : null;
    bulkDelete.isEditing = show;
    bulkDelete.result = null;
    bulkDelete.record = null;
  },
  [DELETE_ADMIN_ASSETS_REQUEST] : ({bulkDelete}: AdminAssetsState) => {
    bulkDelete.loading = true;
    bulkDelete.error = bulkDelete.result = bulkDelete.record = null;
  },
  [DELETE_ADMIN_ASSETS_RESPONSE] : ({bulkDelete}: AdminAssetsState, {assets}: AdminAssetAction) => {
    bulkDelete.loading = false;
    bulkDelete.isEditing = false;
    bulkDelete.result = assets;
    bulkDelete.record = assets;
  },
  [DELETE_ADMIN_ASSETS_ERROR] : ({bulkDelete}: AdminAssetsState, {error}: AdminAssetAction) => {
    bulkDelete.loading = false;
    bulkDelete.error = error;
  },
  [SET_CURRENT_MOVE_TO_ACCOUNT] : ({movedToAccount}: AdminAssetsState, {account}) => {
    movedToAccount.selectedAccount = account;
  },
  [SET_ADMIN_ASSETS_GRID_FILTER_MODEL] : ({grid}: AdminAssetsState, {model}) => {
    grid.filterModel = model;
  }
};

/**
 * defines all reducers for actions of interest to the this reducer ...
 */
export default produce((draft: PageState = initialState, action) => {
  reducers?.[action.type]?.(draft, action);
  return draft;
});
